import { defineStore } from 'pinia';
import type { Session, User } from '@supabase/supabase-js';
import { useDb } from '@/composables/useDb';

export type AppRole = 'admin' | 'production' | 'lab' | 'viewer';

interface JwtPayload {
  app_role?: AppRole;
}

function decodeRole(accessToken: string | undefined): AppRole {
  if (!accessToken) return 'viewer';
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1])) as JwtPayload;
    return payload.app_role ?? 'viewer';
  } catch {
    return 'viewer';
  }
}

interface State {
  session: Session | null;
  user: User | null;
  role: AppRole;
  ready: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    session: null,
    user: null,
    role: 'viewer',
    ready: false,
  }),
  actions: {
    async init() {
      const sb = useDb();
      const { data } = await sb.auth.getSession();
      this.applySession(data.session);
      sb.auth.onAuthStateChange((_event, session) => this.applySession(session));
      this.ready = true;
    },
    applySession(session: Session | null) {
      this.session = session;
      this.user = session?.user ?? null;
      this.role = decodeRole(session?.access_token);
    },
    async signInWithMagicLink(email: string, redirectTo?: string) {
      const sb = useDb();
      return sb.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      });
    },
    async signOut() {
      const sb = useDb();
      await sb.auth.signOut();
    },
  },
});
